/**
 * pasteDraft.js - a whole auction, read off the clipboard.
 *
 * 🔴 THIS IS THE FRONT DOOR NOW, AND THE SCREENSHOT READER IS THE SIDE ENTRANCE.
 * Ben, Sep 1 2026: "If I go to my yahoo league on PC and just hit control A and
 * copy the draft results, would that be easy for you to get the results from?"
 * It is not just easy, it is strictly better than anything a camera can do. The
 * page carries, on every row, the four things a grade needs and an image only
 * hints at: the player's full name, his real position, the exact price, and -
 * the part no screenshot of one roster ever gives - WHICH TEAM BOUGHT HIM. One
 * paste is the entire league, every seat, at full accuracy. No OCR, no $22 read
 * as $2, no kicker lost to a coloured pill.
 *
 * So the ambition here is different from the reader's. The reader tries to
 * rescue a bad picture of one roster. This tries to lose nothing from a perfect
 * transcript of ten.
 *
 * 🔴 THE CLIPBOARD CONTAINS THE WHOLE PAGE, NOT THE TABLE. Ctrl-A takes the nav
 * bar, the ad slots, the league header, the "Nomination Order" table underneath
 * and whatever else the site felt like rendering. So nothing here is
 * line-oriented or position-dependent: the parser hunts the SHAPE of a pick row
 * anywhere in the text and ignores everything that is not one. That is why the
 * fixture keeps its chrome.
 *
 * 🔴 THE CELL SEPARATOR IS NOT KNOWABLE IN ADVANCE. Copying an HTML table gives
 * tabs in some browsers and newlines in others, and the same user gets both on
 * different machines. Every gap below therefore accepts "tabs and spaces, with
 * at most one newline in the middle", which covers both without letting a row
 * swallow the row beneath it.
 *
 * 🔴 A ROW WITH A SUSPECT OWNER LOSES THE OWNER, NEVER THE PICK. If the team
 * column is missing or the capture ran into the next pick, the pick still
 * counts - it just goes to an unknown seat. Dropping a $61 running back because
 * the fourth column was odd would be losing real data to protect a label.
 */

import { fillLadderFromRead } from "./ladderFill.js";
import { CONFIDENT, matchPoolName } from "./poolMatch.js";

/** Yahoo's own words for the positions, plus what other sites call the same. */
const POS_ALIAS = {
  QB: "QB",
  RB: "RB",
  WR: "WR",
  TE: "TE",
  K: "K",
  PK: "K",
  DEF: "DEF",
  DST: "DEF",
  "D/ST": "DEF",
  D: "DEF",
};

/** Tabs and spaces, with at most one line break in the middle. See the header. */
const GAP = "[ \\t]*(?:\\r?\\n)?[ \\t]*";

/**
 * The player cell: a name with his real team and position in brackets.
 *
 * 🔴 THE BRACKETS ARE THE ANCHOR, AND THAT IS A DELIBERATE LIMIT. A looser
 * pattern - "a capitalised name near a dollar figure" - would find players in
 * ad copy, in "Waiver Wire: 5 Adds", in the site's own promo rail. This one
 * finds a cell that a fantasy site deliberately printed as a player. When a
 * platform turns out to write its cells another way, the answer is to add that
 * shape here against a real paste, never to relax this one and hope.
 */
const CELL =
  "([^\\t\\n(]{2,60}?)" +
  "\\s*\\(\\s*([A-Za-z]{2,3})\\s*[-‐-―]\\s*([A-Za-z/]{1,4})\\s*\\)";

/**
 * LAYOUT A - one flat table with a team column. Yahoo, and verified against it.
 *
 * The pick index anchors the row, so a price is allowed to be absent: a snake
 * draft recap in this layout still parses, just without a market.
 */
const NUMBERED = new RegExp("(\\d{1,3})\\." + GAP + CELL + "(?:" + GAP + "\\$\\s*(\\d{1,4}))?", "g");

/**
 * LAYOUT B - a section per team, headed by the team's name, rows underneath.
 *
 * 🔴 THIS IS THE SHAPE ESPN'S DRAFT RECAP USES, AND IT IS NOT VERIFIED AGAINST
 * A REAL ESPN PAGE. Sep 1 2026: ESPN's recap is behind a login and its robots
 * file refuses fetching, so what is implemented here is the STRUCTURE - picks
 * grouped under a heading rather than carrying an owner column - and not any
 * claim about ESPN's exact cells. The structure is worth having on its own: it
 * is also how a Sleeper draft board and most "by team" exports read. Whether
 * ESPN's player cell matches CELL above is an open question that one real paste
 * answers, and the diagnostic below exists to get that paste back.
 *
 * With no index to anchor a row, the PRICE IS REQUIRED here. It is the only
 * thing separating a pick from a line of prose with a name in it.
 */
const UNNUMBERED = new RegExp(CELL + GAP + "\\$\\s*(\\d{1,4})", "g");

// A Yahoo league heading: the name, then its id in parentheses. A literal
// rather than the string form the cell patterns above use, because it needs no
// interpolation and a literal is the form that cannot lose a backslash.
const LEAGUE_NAME = /^[ \t]*(\S[^\n]{0,79}?)[ \t]*\(ID#\s*\d+\)[ \t]*$/m;
const OWNER_AFTER = /^[ \t]*(?:\r?\n)?[ \t]*([^\t\n]{1,60})/;

/**
 * Whether a captured fourth column is a team name or the wreckage of one.
 *
 * The failure this guards is specific and quiet: on a page with no team column
 * the capture runs on and picks up the next row's index, so every pick would be
 * owned by a seat called "2." and the league would come out with 160 teams.
 */
export function looksLikeOwner(s) {
  const t = String(s || "").trim();
  if (!t) return false;
  if (t.length > 40) return false;
  if (/^\d{1,3}\.?$/.test(t)) return false;       // a pick index
  if (/^\$/.test(t)) return false;                 // a price column
  if (/\(\s*[A-Za-z]{2,3}\s*[-‐-―]/.test(t)) return false; // a player cell
  if (/^(pick|player|salary|team|round|cost|price|owner)$/i.test(t)) return false;
  return true;
}

/** "$200" after the words that introduce it, when the page bothers to say. */
export function budgetFromPaste(text) {
  const m = /starting\s+budget[\s\S]{0,400}?\$\s*(\d{2,4})/i.exec(String(text || ""));
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 10 ? n : null;
}

/**
 * Whether a line could be the heading of a team's section.
 *
 * Deliberately permissive, because team names are whatever people typed, and
 * deliberately fenced by the caller: a heading only becomes a seat if picks
 * actually land under it and enough of them do to be a roster. A permissive
 * test with a strict gate behind it beats a strict test that rejects
 * "AUCTION MY ZOLO'S".
 */
export function looksLikeHeading(line) {
  const t = String(line || "").trim();
  if (!t || t.length > 40) return false;
  if (t.includes("$")) return false;
  if (/\(\s*[A-Za-z]{2,3}\s*[-‐-―]/.test(t)) return false; // a player cell
  if (!/[A-Za-z]/.test(t)) return false;
  if (/^(pick|player|salary|team|round|cost|price|owner|bid|pos|position|nfl)$/i.test(t)) return false;
  return true;
}

/** Where every line begins, so a match offset can be turned back into a line. */
function lineStarts(src) {
  const at = [0];
  for (let i = 0; i < src.length; i += 1) if (src[i] === "\n") at.push(i + 1);
  return at;
}

/** The index of the line containing `offset`. */
function lineAt(starts, offset) {
  let lo = 0;
  let hi = starts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (starts[mid] <= offset) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Every pick the text contains, in page order, from LAYOUT A.
 *
 * Deliberately dumb about meaning: it reports what it found and leaves judging
 * to the caller. `pos` is normalised because the rest of the app has one
 * vocabulary and "D/ST" is not in it; nothing else is touched.
 */
export function parsePicks(text) {
  const src = String(text || "");
  const out = [];
  NUMBERED.lastIndex = 0;
  let m;
  while ((m = NUMBERED.exec(src)) !== null) {
    const pos = POS_ALIAS[String(m[4] || "").toUpperCase()];
    if (!pos) continue;
    const price = m[5] == null ? null : Number(m[5]);
    // The cursor only advances past the fourth column once it has been judged a
    // team name. See OWNER_AFTER's note on why that matters.
    const after = OWNER_AFTER.exec(src.slice(NUMBERED.lastIndex, NUMBERED.lastIndex + 100));
    let owner = null;
    if (after && looksLikeOwner(after[1])) {
      owner = after[1].trim();
      NUMBERED.lastIndex += after[0].length;
    }
    out.push({
      pick: Number(m[1]),
      player: String(m[2]).trim(),
      team: String(m[3]).toUpperCase(),
      pos,
      price: Number.isFinite(price) ? price : null,
      owner,
      at: m.index,
    });
  }
  return out;
}

/**
 * Every pick the text contains, from LAYOUT B, with the heading above each one
 * as its owner.
 *
 * 🔴 A HEADING ONLY BECOMES A SEAT IF A ROSTER LANDS UNDER IT. The nearest
 * plausible line above a pick is a weak signal on its own - on a page with ads
 * between the sections it would produce a league of one-man teams named after
 * banner copy. So the assignment is made first and JUDGED SECOND, by the caller
 * below: sections are kept only when several of them each collected enough
 * picks to be a roster. A layout that cannot clear that bar is reported as
 * unreadable rather than delivered as a strange little league.
 */
export function parseSectioned(text) {
  const src = String(text || "");
  const lines = src.split(/\r?\n/);
  const starts = lineStarts(src);
  const out = [];
  UNNUMBERED.lastIndex = 0;
  let m;
  while ((m = UNNUMBERED.exec(src)) !== null) {
    const pos = POS_ALIAS[String(m[3] || "").toUpperCase()];
    if (!pos) continue;
    // Walk back up the page for the last line that could be a team's name and
    // is not itself part of a pick row.
    let owner = null;
    for (let i = lineAt(starts, m.index) - 1; i >= 0 && i > lineAt(starts, m.index) - 60; i -= 1) {
      if (looksLikeHeading(lines[i])) {
        owner = lines[i].trim();
        break;
      }
    }
    out.push({
      pick: out.length + 1,
      player: String(m[1]).trim(),
      team: String(m[2]).toUpperCase(),
      pos,
      price: Number(m[4]),
      owner,
      at: m.index,
    });
  }
  return out;
}

/**
 * The picks, from whichever layout the text turns out to be in.
 *
 * 🔴 LAYOUT A IS TRIED FIRST AND WINS ON A TIE, because it is the one verified
 * against a real page. Sectioning is the fallback, and it only displaces
 * Layout A when Layout A found materially less - a page with no pick indices
 * gives Layout A nothing, which is exactly when the fallback is wanted.
 */
export function pickAnyLayout(text) {
  const flat = parsePicks(text);
  const owned = flat.filter((p) => p.owner).length;
  if (owned >= 10) return { picks: flat, layout: "table" };
  const sectioned = parseSectioned(text);
  const grouped = new Map();
  sectioned.forEach((p) => {
    if (!p.owner) return;
    grouped.set(p.owner, (grouped.get(p.owner) || 0) + 1);
  });
  // Two seats, five men each: below that it is noise wearing a league's shape.
  const seats = [...grouped.values()].filter((n) => n >= 5).length;
  if (seats >= 2 && sectioned.length > flat.length) {
    return { picks: sectioned, layout: "sections" };
  }
  return { picks: flat, layout: "table" };
}

/**
 * Resolve one pick against the player pool.
 *
 * 🔴 THE NAME IS EXACT HERE AND THE MATCHER STILL RUNS. It would be tempting to
 * trust a typed page and skip straight to the pool by name, and for 155 of the
 * 160 picks that works. The five it does not are the point: the pool is this
 * app's own player list, and a man the pool has never heard of cannot be graded
 * whatever the page calls him. Running the same matcher the screenshot path runs
 * means an unrecognised name arrives as an unmatched row the user can see rather
 * than a silent hole in a roster.
 *
 * Position is passed as a hard filter and the NFL team as a tie-break, both of
 * which the page gives for free and a screenshot mostly does not. That is why
 * this path produces almost no uncertain rows.
 */
function sameTeam(a, b) {
  if (!a || !b) return false;
  return String(a).toUpperCase() === String(b).toUpperCase();
}

function resolve(pick) {
  const match = matchPoolName(pick.player, { pos: pick.pos, team: pick.team });
  if (!match.player) return null;
  return {
    player: match.player,
    pos: match.pos || pick.pos,
    price: pick.price,
    // 🔴 THE NFL TEAM IS CORROBORATION, NOT A TIE-BREAK, AND IT SETTLES THE
    // DEFENSES. Yahoo prints "Seahawks (Sea - DEF)" and the pool calls him
    // "Seattle Seahawks", which scores below the threshold on the name alone -
    // so all ten defenses in a ten-team league arrived flagged for review, ten
    // rows of busywork over the one position nobody is ever unsure about. When
    // the page's own team abbreviation matches the pool entry's team and the
    // position group already agreed, there is nothing left to be unsure about.
    confident: match.score >= CONFIDENT || sameTeam(match.team, pick.team),
    candidates: match.candidates || [],
    order: pick.pick,
  };
}

/**
 * What a paste that did not work looks like, in enough detail to fix it.
 *
 * 🔴 THIS EXISTS BECAUSE THE NEXT PLATFORM IS ALWAYS UNKNOWN. Sep 1 2026: Yahoo
 * is verified, ESPN cannot be reached from where this was written, and Sleeper,
 * NFL.com, MFL and Fantrax are all sitting behind the same wall. The choice is
 * between guessing their formats - which is how the screenshot reader ended up
 * confidently pointing people at a page with no prices on it - and making a
 * failure carry its own evidence back. This is the second one.
 *
 * WHAT IT COLLECTS AND WHY: how much text there was (a truncated copy is a
 * common and invisible failure), how many dollar figures the page contains (a
 * page with none is not a draft recap and no parser will save it), and three
 * sample lines that HAVE a dollar figure in them - which is, precisely, the
 * shape that has to be taught. Names of real people are not needed for that and
 * the samples are shown to the user before they are sent anywhere.
 */
export function diagnose(text, picks, teams) {
  const src = String(text || "");
  const lines = src.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const priced = lines.filter((l) => /\$\s*\d/.test(l));
  return {
    lines: lines.length,
    dollars: (src.match(/\$\s*\d/g) || []).length,
    picksFound: picks.length,
    teamsFound: teams.length,
    // The three that matter: if the parser missed a real draft, the shape it
    // needs to learn is in here.
    samples: priced.slice(0, 3).map((l) => l.slice(0, 120)),
  };
}

/**
 * The whole paste, turned into teams.
 *
 * @returns {{
 *   ok: boolean,
 *   teams: Array<{ name: string, entries: Array<object> }>,
 *   picks: Array<object>,
 *   unmatched: Array<{ raw: string, pos: string, price: number|null, owner: string|null }>,
 *   budget: number|null,
 *   teamCount: number,
 *   rosterSize: number,
 *   unpriced: boolean
 * }}
 *
 * 🔴 THE SEAT ORDER IS THE ORDER THE PAGE NOMINATES IN, not alphabetical and
 * not by spend. A user looking for their own team scans the list for their name,
 * and a list in the order their league's own page prints it is the one they can
 * scan. Sorting by spend would also quietly rank the room before anybody asked
 * for a ranking.
 *
 * 🔴 AN UNOWNED PICK IS NOT A TEAM. Rows whose owner column did not survive are
 * kept in `picks` and left out of `teams`, rather than being pooled into a seat
 * called "Unknown" that would then be graded as if somebody played it.
 */
export function parseDraftPaste(text) {
  const { picks, layout } = pickAnyLayout(text);
  const unmatched = [];
  const order = [];
  const bySeat = new Map();

  picks.forEach((p) => {
    const entry = resolve(p);
    if (!entry) {
      unmatched.push({ raw: p.player, pos: p.pos, price: p.price, owner: p.owner });
      return;
    }
    if (!p.owner) return;
    if (!bySeat.has(p.owner)) {
      bySeat.set(p.owner, []);
      order.push(p.owner);
    }
    bySeat.get(p.owner).push(entry);
  });

  const teams = order.map((name) => ({ name, entries: bySeat.get(name) }));
  // The roster size the room actually plays, taken as the commonest seat size
  // rather than the largest: one seat left a man short should not stretch the
  // league's shape, and one seat with a keeper should not either.
  const sizes = teams.map((t) => t.entries.length).sort((a, b) => a - b);
  const rosterSize = sizes.length ? sizes[Math.floor(sizes.length / 2)] : 0;

  return {
    ok: teams.length >= 2 && rosterSize >= 2,
    teams,
    picks,
    layout,
    unmatched,
    diagnostic: diagnose(text, picks, teams),
    budget: budgetFromPaste(text),
    teamCount: teams.length,
    rosterSize,
    // A snake draft pastes with no salary column at all, so every price is
    // absent rather than wrong. The caller says so instead of grading a market
    // nobody played in.
    unpriced: picks.length > 0 && picks.every((p) => p.price == null),
  };
}

/**
 * The FIXED set of reasons a paste can fail to read.
 *
 * 🔴 A CLOSED ENUM, AND IT CARRIES NO TEXT. This is what an analytics event is
 * allowed to say about a paste that did not work, and the whole point of a
 * closed list is that a paste's own content can never become a property: a
 * pasted draft is nothing BUT player, team and manager names, so any fragment
 * of it — truncated, hashed, sampled, however carefully — is the wrong thing to
 * send. The three sample lines the panel shows the user are shown TO THE USER
 * and go nowhere; see diagnose().
 *
 * Every value is derivable from fields the parser already computes, so this
 * names distinctions the code makes rather than inventing new ones. Each one is
 * a different fix on our side, which is the test of whether a reason is worth
 * having:
 *
 *   empty          nothing but whitespace arrived — a copy that did not take
 *   no_prices      text, but not one dollar figure in it. The file's own note:
 *                  a page with no prices is not a draft recap, and no parser
 *                  will save it. Usually a snake draft or the wrong screen.
 *   unrecognized   prices are there and no row read at all — an unknown
 *                  platform's layout, the case worth teaching the parser
 *   no_players     rows read, not one of them matched a player we carry
 *   no_teams       players matched, but under two seats came out — the owner
 *                  column did not survive the copy
 *   short_rosters  two or more seats, each holding under two players
 */
export const PASTE_FAIL_REASONS = [
  "empty",
  "no_prices",
  "unrecognized",
  "no_players",
  "no_teams",
  "short_rosters",
];

/**
 * Why this read failed, as one of PASTE_FAIL_REASONS, or null when it did not.
 *
 * First match wins and the order IS the definition, the same way
 * buildArchetype's rules are ordered: a paste can satisfy two of these at once
 * (empty text has no prices either) and first-match makes that deterministic
 * rather than a matter of which check a later edit happens to put first. Read
 * the list as "the earliest thing that went wrong".
 *
 * The final branch is a total fallback, so this function cannot return null for
 * a failed read: `ok` is `teams >= 2 && rosterSize >= 2`, and the two clauses
 * are the last two reasons.
 */
export function pasteFailReason(read) {
  if (!read || read.ok) return null;
  const d = read.diagnostic || {};
  if (!d.lines) return "empty";
  const picks = read.picks || [];
  if (picks.length === 0) return d.dollars ? "unrecognized" : "no_prices";
  const teams = read.teams || [];
  if (teams.length === 0) return (read.unmatched || []).length >= picks.length ? "no_players" : "no_teams";
  if (teams.length < 2) return "no_teams";
  return "short_rosters";
}

/** One sentence about what came out, for the panel above the button. */
export function pasteSummary(read) {
  if (!read || !read.ok) {
    return "I could not find a draft in that. Copy the whole Draft Results page and paste it here.";
  }
  const parts = [`Found ${read.teamCount} teams and ${read.picks.length} picks.`];
  if (read.budget) parts.push(`$${read.budget} budgets.`);
  if (read.unmatched.length > 0) {
    parts.push(
      read.unmatched.length === 1
        ? "1 pick I do not have a player for."
        : `${read.unmatched.length} picks I do not have players for.`
    );
  }
  return parts.join(" ");
}

/**
 * Which site this was copied from, if it says so.
 *
 * Worth reading because it answers the scoring question for free: a Yahoo page
 * means Yahoo's defaults, and asking somebody to describe scoring they have
 * never looked at produces a worse answer than the platform's own preset. The
 * user still confirms it - this only decides what the box starts on.
 */
export function platformFromPaste(text) {
  const t = String(text || "");
  if (/yahoo/i.test(t)) return "Yahoo Default";
  if (/\bESPN\b/.test(t)) return "ESPN Default";
  if (/sleeper/i.test(t)) return "Sleeper Default";
  return null;
}

/**
 * What the league is CALLED, when the page says so.
 *
 * 🔴 ONE PATTERN, AND IT IS YAHOO'S. A Yahoo fantasy page carries its league
 * name in a heading of the form "Strong Island Fantasy (ID# 538200)", which is
 * specific enough to read with no guessing: the id in parentheses is the proof
 * that the words in front of it are a league name and not site chrome. Nothing
 * else is attempted. ESPN's recap header has no such marker, and inventing a
 * heuristic for it would mean naming somebody's league after a nav item; a
 * league with no readable name is ASKED about on the confirm screen instead,
 * which is a better answer than a wrong one.
 *
 * The id itself is discarded here and never stored: this returns the name only.
 *
 * @returns {string|null} the league's name, trimmed, or null
 */
export function leagueNameFromPaste(text) {
  const m = LEAGUE_NAME.exec(String(text || ""));
  if (!m) return null;
  const name = m[1].trim();
  return name.length > 0 ? name : null;
}


/**
 * The lineup shape a paste can actually prove, and the part it cannot.
 *
 * 🔴 WHAT A DRAFT LIST CAN AND CANNOT TELL YOU, because the difference is the
 * whole reason there is a confirmation screen at all:
 *
 *   PROVEN. Every seat's kicker and defense count. If all ten teams rostered
 *   exactly one kicker, the league starts one kicker and benches none - nobody
 *   carries a backup kicker, and nobody is short one. Same for defense. These
 *   are read straight off the counts and are as certain as the prices.
 *
 *   PROVEN. One-quarterback versus superflex. In a superflex room NOBODY ends
 *   the draft with a single quarterback; the position is too valuable to punt
 *   when two can start. So a minimum of one across the room settles it. (A
 *   minimum of two does not prove superflex on its own, which is exactly the
 *   kind of near-miss the confirm screen exists to catch.)
 *
 *   NOT KNOWABLE, EVER. How many of those running backs and receivers START.
 *   A draft list records who was bought, never which slot he was bought for,
 *   and no amount of cleverness recovers it. It changes projected points and
 *   the position grades - though not the market grade, which is priced. So it
 *   is asked, not guessed, and it is the ONLY thing the screen really needs.
 *
 * Returns the house shape adjusted by what was proven, plus `proven`: the list
 * of fields the screen may state as fact rather than ask about.
 */
export function shapeFromPaste(read, base) {
  const housePositions = { ...(base && base.positions ? base.positions : {}) };
  const teams = read.teams || [];
  const countOf = (t, pos) => t.entries.filter((e) => e.pos === pos).length;
  const minOf = (pos) => (teams.length ? Math.min(...teams.map((t) => countOf(t, pos))) : null);

  const positions = { ...housePositions };
  const proven = [];

  const minK = minOf("K");
  if (minK != null && minK === Math.max(...teams.map((t) => countOf(t, "K")))) {
    positions.K = minK;
    proven.push("K");
  }
  const minDef = minOf("DEF");
  if (minDef != null && minDef === Math.max(...teams.map((t) => countOf(t, "DEF")))) {
    positions.DEF = minDef;
    proven.push("DEF");
  }
  const minQb = minOf("QB");
  if (minQb === 1) {
    positions.QB = 1;
    positions.SFLEX = 0;
    proven.push("QB");
  }

  // The starters nobody can read stay at the house shape, but never above what
  // the thinnest roster in the room actually holds: proposing three receiver
  // slots to a league whose leanest team bought two is proposing a lineup that
  // team could not field.
  ["RB", "WR", "TE"].forEach((pos) => {
    const m = minOf(pos);
    if (m != null) positions[pos] = Math.min(Number(housePositions[pos]) || 0, m);
  });

  // Whatever is left over is bench. Computed last, so the total always matches
  // the roster the page actually shows.
  const starters = Object.keys(positions)
    .filter((k) => k !== "BN")
    .reduce((a, k) => a + (Number(positions[k]) || 0), 0);
  positions.BN = Math.max(0, (read.rosterSize || 0) - starters);

  return { positions, proven };
}

/**
 * The league settings a paste implies, laid over the ones already in force.
 *
 * 🔴 IT SETS WHAT THE PAGE STATES OR PROVES, AND ASKS ABOUT THE REST. The team
 * count and the budget are printed facts. The kicker slot, the defense slot and
 * one-quarterback-versus-superflex are provable from the counts (see
 * shapeFromPaste). The starting lineup is not knowable from any draft list, so
 * it arrives at the house shape for a person to confirm rather than arriving
 * as a silent guess inside a grade.
 *
 * 🔴 THE ROSTER TOTAL ALWAYS MATCHES THE PAGE. Bench is whatever is left after
 * the starters, so a sixteen-man roster is sixteen slots however the lineup is
 * later adjusted, and nobody ever overflows.
 */
export function settingsForPaste(read, base, text) {
  const { positions } = shapeFromPaste(read, base);
  const platform = platformFromPaste(text);
  const out = {
    ...(base || {}),
    teams: read.teamCount || (base && base.teams) || 0,
    budget: read.budget || (base && base.budget) || 200,
    positions,
  };
  if (platform) out.platform = platform;
  // 🔴 THE NAME IS LOCAL, ALWAYS. It reaches the page heading and the saved
  // room's chip in this browser and it never enters the corpus payload -
  // autoSubmitRoom rebuilds that body field by field for exactly this reason,
  // the same way it drops the manager names the paste read off the transcript.
  const name = leagueNameFromPaste(text);
  if (name) out.name = name;
  return out;
}

/**
 * Every seat in the paste, as a ladder the entry form can draw.
 *
 * Each seat is filled from empty rather than onto whatever was there: a paste
 * is a complete statement of a league, so merging it into a half-typed roster
 * would produce a room that is part transcript and part memory with no way to
 * tell which row is which.
 */
export function laddersFromPaste(read, positions) {
  return (read.teams || []).map((t) => {
    const out = fillLadderFromRead(t.entries, positions, {});
    return { name: t.name, ladder: out.ladder, overflow: out.overflow || [] };
  });
}
